import { useToast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi } from '../services/users.service';

interface UpdateUserSettingsParams {
    userId: string;
    settings: Record<string, unknown>;
    noRenderModal?: boolean;
}

export const useUpdateUserSettings = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async ({ userId, settings }: UpdateUserSettingsParams) => {
            return usersApi.updateUserSelfOrganizationSettings(userId, settings);
        },
        onMutate: async ({ userId: _userId, settings }) => {
            await queryClient.cancelQueries({ queryKey: ['userInfo'] });
            const previousUserInfo = queryClient.getQueryData<IAuthMeV2OutputDto>(['userInfo']);

            // Optimistically update userInfo in cache
            if (previousUserInfo && previousUserInfo.currentOrganization) {
                const orgId = previousUserInfo.currentOrganization;
                const prevSettings =
                    typeof previousUserInfo.settings === 'object' && previousUserInfo.settings !== null
                        ? previousUserInfo.settings
                        : {};
                const prevOrgSettings =
                    typeof prevSettings[orgId] === 'object' && prevSettings[orgId] !== null ? prevSettings[orgId] : {};
                queryClient.setQueryData<IAuthMeV2OutputDto>(['userInfo'], {
                    ...previousUserInfo,
                    settings: {
                        ...prevSettings,
                        [orgId]: {
                            ...(prevOrgSettings as Record<string, unknown>),
                            ...settings,
                        },
                    },
                });
            }

            return { previousUserInfo };
        },
        onError: (error, _variables, context) => {
            // Rollback cache to previous state
            if (context?.previousUserInfo) {
                queryClient.setQueryData(['userInfo'], context.previousUserInfo);
            }
            // toast({
            //     title: 'Error',
            //     description: 'Failed to save your preferences. Please try again.',
            //     variant: 'destructive',
            // });
            console.error('Error updating user settings:', error);
        },
        onSuccess: () => {
            // toast({
            //     title: t('onboarding.preferencesModal.title'),
            //     description: t('onboarding.preferencesModal.description'),
            // });
        },
        onSettled: (_data, _error, variables) => {
            if (variables.noRenderModal === false) {
                queryClient.invalidateQueries({ queryKey: ['userInfo'] });
            }
        },
    });
};
